import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/store/store";
import type { Feature } from "@/model/features";
import { setFeatures } from "@/store/feature-list-slice";
import { QueryKeys } from "@/constants";
import { features } from "@/services/home/features";
import useUserInfo from "@/hooks/use-user-info";

// ---------------------------------------------------------------------------
// Bootstrap — fetches the plan-keyed feature list and syncs byPlan into the
// store. Called internally by useFeatures; also exported for callers that need
// raw loading / error / refetch state (e.g. AppRouter).
// ---------------------------------------------------------------------------

export function useFeatureBootstrap() {
  const dispatch = useDispatch();
  const userInfo = useUserInfo();

  // activePlanCode comes directly from planCoverageInfo — no separate v2
  // demographics slice needed. plan switches in the old store flow here
  // automatically via useSelector reactivity.
  const activePlanCode = useSelector(
    (state: RootState) =>
      state.planCoverageInfo?.activeCoverage?.claimCoverageCode ?? null,
  );

  const featuresQuery = useQuery({
    queryKey: [QueryKeys.FEATURE_LIST, userInfo.memberId],
    queryFn: () => features(userInfo),
    retry: (failCount, error: any) => error?.status !== 401 && failCount < 2,
    staleTime: 30 * 60 * 1000,
  });

  // Sync byPlan into featureList slice once the query settles
  useEffect(() => {
    if (!featuresQuery.isSuccess || !featuresQuery.data) return;
    dispatch(setFeatures({ byPlan: featuresQuery.data.data }));
  }, [featuresQuery.isSuccess]);

  return {
    isLoading: featuresQuery.isLoading || !activePlanCode,
    isError: featuresQuery.isError,
    isReady: featuresQuery.isSuccess && !!activePlanCode,
    featError: featuresQuery.error,
    refetchFeatures: featuresQuery.refetch,
  };
}

// ---------------------------------------------------------------------------
// Main export — returns the active plan's Feature[] from the store.
// Drop-in replacement for the old useFeatures(userInfo) call.
// ---------------------------------------------------------------------------

export function useFeatures() {
  const { isLoading, isError, isReady } = useFeatureBootstrap();

  // Read activePlan directly from the existing planCoverageInfo slice —
  // same source of truth used everywhere else in the app.
  const activePlanCode = useSelector(
    (state: RootState) =>
      state.planCoverageInfo?.activeCoverage?.claimCoverageCode ?? null,
  );
  const byPlan = useSelector(
    (state: RootState) => state.featureList?.byPlan ?? {},
  );

  const data = useMemo((): Feature[] => {
    if (!activePlanCode || !byPlan[activePlanCode]) return [];
    return byPlan[activePlanCode];
  }, [byPlan, activePlanCode]);

  return { isLoading, isError, isSuccess: isReady, data };
}

// ---------------------------------------------------------------------------
// Gate hook — returns true only when featureList is ready AND the given route
// path exists in the active plan's feature list. Use in query `enabled` flags
// to prevent fetching data for features not available in the current plan.
// ---------------------------------------------------------------------------

export function useIsFeatureEnabled(webRoute: string): boolean {
  const activePlanCode = useSelector(
    (state: RootState) =>
      state.planCoverageInfo?.activeCoverage?.claimCoverageCode ?? null,
  );
  const byPlan = useSelector(
    (state: RootState) => state.featureList?.byPlan ?? {},
  );
  const featureListStatus = useSelector(
    (state: RootState) => state.featureList?.status,
  );

  if (featureListStatus !== "succeeded" || !activePlanCode) return false;
  return (byPlan[activePlanCode] ?? []).some(
    (f) => f.action?.web_route === webRoute,
  );
}
